import React, { useCallback, useEffect, useRef } from 'react';

import { DEFAULT_ZOOM, KEYBOARD_ZOOM_STEP, MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from '../../constants';
import usePlannerStore from '../../store';

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

// Per-event wheel/pinch tuning. The browser reports a `deltaY` whose magnitude
// reflects how fast the user pinches; we multiply ZOOM_STEP by that (capped) so
// fast gestures zoom faster. WHEEL_DELTA_SCALE keeps small deltas reasonable.
const MAX_WHEEL_DELTA = 4;
const WHEEL_DELTA_SCALE = 0.5;

/** Cursor anchor (relative to the planner container) captured during a wheel-zoom,
 *  so the cell-width layout effect can pin that point instead of the left edge. */
export interface ZoomAnchor {
  x: number;
  y: number;
}

export const useZoom = (disableZoom = false) => {
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const setZoomLevel = usePlannerStore(store => store.setZoomLevel);

  // Populated by the wheel handler right before a zoom change; consumed (and cleared)
  // by planner.tsx's scroll-rescale layout effect. Button/keyboard zoom leave it null
  // so that path pins the viewport instead of a cursor point.
  const zoomAnchorRef = useRef<ZoomAnchor | null>(null);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (disableZoom) return;
      if (e.ctrlKey || (e.metaKey && !disableZoom)) {
        e.preventDefault();

        const container = document.getElementById('planner-container');
        if (!container) return;

        // Scale the zoom delta by the gesture magnitude so a fast trackpad pinch
        // (large |deltaY|) zooms faster than a slow one. Capped per event so a single
        // huge delta can't jump the whole range at once.
        const magnitude = Math.min(Math.abs(e.deltaY), MAX_WHEEL_DELTA);
        const zoomDelta = (e.deltaY < 0 ? 1 : -1) * ZOOM_STEP * magnitude * WHEEL_DELTA_SCALE;
        const newZoom = clampZoom(zoomLevel + zoomDelta);

        if (newZoom !== zoomLevel) {
          // Record the cursor point so the layout effect (which runs after the new
          // cell width is laid out) can keep that exact point fixed. Doing the scroll
          // adjustment here would race the layout effect and double-apply.
          const rect = container.getBoundingClientRect();
          zoomAnchorRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          setZoomLevel(newZoom);
        }

        return false;
      }
    },
    [zoomLevel, setZoomLevel, disableZoom]
  );

  useEffect(() => {
    if (disableZoom) return;
    const handleDisableZoom = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    // Handle CMD/CTRL +/-/0 to zoom the planner instead of the browser.
    // Read latest zoom via getState() to avoid stale closures (deps = [disableZoom] only).
    const handleKeyZoom = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;

      // Don't hijack +/-/0 while the user is typing in an input/textarea/editable field.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      ) {
        return;
      }
      // The viewport-center re-anchor is handled by planner.tsx's scrollLeft
      // rescale effect, which fires whenever cellWidthPx (zoomLevel) changes.
      const { zoomLevel: currentZoom, setZoomLevel: setZoom } = usePlannerStore.getState();

      // '=' is the unshifted '+' key.
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setZoom(clampZoom(currentZoom + KEYBOARD_ZOOM_STEP));
      } else if (event.key === '-') {
        event.preventDefault();
        setZoom(clampZoom(currentZoom - KEYBOARD_ZOOM_STEP));
      } else if (event.key === '0') {
        event.preventDefault();
        setZoom(DEFAULT_ZOOM);
      }
    };

    document.addEventListener('wheel', handleDisableZoom, { passive: false });
    document.addEventListener('keydown', handleKeyZoom);

    return () => {
      document.removeEventListener('wheel', handleDisableZoom);
      document.removeEventListener('keydown', handleKeyZoom);
    };
  }, [disableZoom]);

  return { handleWheel, zoomAnchorRef };
};
